import type { ComponentStyleConfig } from '@chakra-ui/theme';
import type { StyleFunctionProps } from '@chakra-ui/theme-tools';

/**
 * Prop the kit's version implementations pass down so the merged style-config
 * can pick the matching styles at render time
 * (e.g. `<Button data-dls-version="2.1" />`). An element that passes nothing
 * falls through to `defaultVersion` below. Chakra v2's `useStyleConfig`
 * forwards every component prop into the `baseStyle`/`variants`/`sizes` style
 * functions, which is what makes this work — same family as the existing
 * `data-visually-disabled` pattern.
 */
const DLS_VERSION_PROP = 'data-dls-version';

interface IVersionedStyleConfigOptions {
  /** Current-version styleConfig — used when no `data-dls-version` matches `previous`. */
  current: ComponentStyleConfig;
  /** Previous-version styleConfig, keyed by the version string the kit passes. */
  previous?: { version: string; config: ComponentStyleConfig };
  /**
   * Which version an element carrying **no** `data-dls-version` renders — in practice, elements
   * imported straight from `@chakra-ui/react`, which never pass through the kit and so are never
   * stamped. Defaults to `current`.
   *
   * Must match the component's `default` in the kit's `versions.ts`, or the same markup renders
   * differently depending on which import it came from. The two are pinned together by
   * `libs/republic/ui/theme/src/heading-versioning.spec.ts`.
   */
  defaultVersion?: string;
}

/** A single `baseStyle`/`variants[k]`/`sizes[k]` entry: style object, function, or array. */
type StyleInterpolation = NonNullable<ComponentStyleConfig['variants']>[string];

/** Chakra's `runIfFn` contract: call functions with props, return objects/arrays as-is. */
const runIfFn = (
  style: StyleInterpolation | undefined,
  props: StyleFunctionProps
) => (typeof style === 'function' ? style(props) : style);

const unionKeys = (
  a?: Record<string, unknown>,
  b?: Record<string, unknown>
): string[] =>
  Array.from(new Set([...Object.keys(a ?? {}), ...Object.keys(b ?? {})]));

/**
 * Merges two versions of a component's Chakra style-config into a single
 * `ComponentStyleConfig` bound to one theme key, so multiple DLS versions can
 * coexist without versioned theme keys or variant-name suffixing.
 *
 * With no `previous`, returns `current` untouched (zero cost for single-version
 * components). With a `previous`, returns a config whose `baseStyle`/`variants`/
 * `sizes` are functions that read `data-dls-version` off the incoming props and
 * delegate to the matching version. `variants`/`sizes` expose the **union** of
 * both versions' keys so either version resolves; `defaultProps` come from
 * whichever version an unstamped element resolves to (per-version defaults are
 * otherwise set by the kit implementation via props).
 * Chakra memoizes resolved styles per props, so the indirection is free.
 *
 * An element with no `data-dls-version` resolves to `current` unless
 * `defaultVersion` says otherwise. Only elements that bypass the kit are
 * unstamped, so that option is how a component keeps its previous version as
 * the default for raw `@chakra-ui/react` call sites.
 *
 * The union of variant/size keys is also what the chakra-cli
 * `--strict-component-types` augmentation reads — while two versions coexist,
 * the generated variant/size union widens to cover both. Per-version narrowing
 * lives in the kit's per-version prop interfaces, not here.
 *
 * File convention (scaffolded only when a `previous` version appears; mirrored
 * in the wallet theme lib):
 *
 * ```
 * component-styles/button/
 *   index.ts   // export const styleConfig = versionedStyleConfig({ current: v2_2, previous: { version: '2.1', config: v2_1 } })
 *   2.2.ts     // current styleConfig
 *   2.1.ts     // previous styleConfig
 * ```
 *
 * Single-version components keep their flat `component-styles/<name>.ts` file.
 */
export const versionedStyleConfig = (
  opts: IVersionedStyleConfigOptions
): ComponentStyleConfig => {
  const { current, previous, defaultVersion } = opts;
  if (!previous) return current;

  const pick = (props: StyleFunctionProps): ComponentStyleConfig =>
    (props[DLS_VERSION_PROP] ?? defaultVersion) === previous.version
      ? previous.config
      : current;

  const mapKeys = (keys: string[], kind: 'variants' | 'sizes') =>
    Object.fromEntries(
      keys.map((key) => [
        key,
        (props: StyleFunctionProps) => runIfFn(pick(props)[kind]?.[key], props),
      ])
    );

  const variantKeys = unionKeys(current.variants, previous.config.variants);
  const sizeKeys = unionKeys(current.sizes, previous.config.sizes);

  return {
    ...current, // carries `parts` (multi-part configs) + anything not overridden below
    baseStyle: (props: StyleFunctionProps) =>
      runIfFn(pick(props).baseStyle, props),
    ...(variantKeys.length
      ? { variants: mapKeys(variantKeys, 'variants') }
      : {}),
    ...(sizeKeys.length ? { sizes: mapKeys(sizeKeys, 'sizes') } : {}),
    defaultProps: (defaultVersion === previous.version
      ? previous.config
      : current
    ).defaultProps,
  } as ComponentStyleConfig; // ponytail: dynamic single|multi config boundary — one cast beats lying about the return of runIfFn
};
